import { useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { useFullScreenHandle } from "react-full-screen";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { LayoutState, MediaQueryLinebreak, MediaQueryMobile, MediaQueryShallow, MediaQuerySmall1, MediaQuerySmall2, MediaQuerySmall3, MediaQuerySmall4, MediaQuerySmall5, PicDimH, PicDimW, PicWidth, Scrolls, ScrollToPosition } from "../Layout";
import { hideKrpano } from "../Util";
import i18n, { getSeries, ui, Workdesc } from "../i18n";
import { change } from "../state/Nav";
import { RootState } from "../store";
import { ImageDesc, itemDataAll } from "./GalleryData";
import GalleryDesktop from "./GalleryDesktop";
import GalleryMobile from "./GalleryMobile";

type Lang = "de" | "en";

const landscape = "landscape";
const portrait = "portrait";

const size = PicWidth;
const wh = size * PicDimW / PicDimH;

// The behavior of the component is controlled by two elements:
//     paintingName: name of the painting shown ("" = no painting)
//     params.serie: name of the series shown ("all" = summary of series)
export default function Gallery() {
    // Init paintingName
    const [paintingName, setPaintingName] = useState("");
    const navigate = useNavigate();

    // Get input params
    const params = useParams();

    const isPainting = (paintingName !== "");
    const isGalleryAll = (params.serie === "all");
    const isNotAll = (params.serie !== "all");

    // Init layout parameters
    const layoutState = new LayoutState();
    layoutState.isMobile = useMediaQuery(MediaQueryMobile);
    layoutState.isSmall1 = useMediaQuery(MediaQuerySmall1);
    layoutState.isSmall2 = useMediaQuery(MediaQuerySmall2);
    layoutState.isSmall3 = useMediaQuery(MediaQuerySmall3);
    layoutState.isSmall4 = useMediaQuery(MediaQuerySmall4);
    layoutState.isSmall5 = useMediaQuery(MediaQuerySmall5);
    layoutState.linebreak = useMediaQuery(MediaQueryLinebreak);
    layoutState.isShallow = useMediaQuery(MediaQueryShallow);

    // Determine UI language
    const lang = useSelector((state: RootState) => state.language.value);

    // Set navigation to current component
    const dispatch = useDispatch();
    dispatch(change("gallery"));

    // Hide virtual tour
    hideKrpano();

    // Scroll to previous position
    if (!isPainting) {
        if (isGalleryAll) {
            ScrollToPosition("galleryAll");
            Scrolls.positions.set("gallerySerie", 0);
        }
        else {
            ScrollToPosition("gallerySerie");
        }
    }

    // Required hooks
    const handle = useFullScreenHandle();
    const theme = useTheme();

    // Construct the net data to be shown: heading and item data
    // Init with the summary series
    let heading = ui[lang].Gallery.heading;
    let itemData = itemDataAll;
    // Set for specific series
    if (isNotAll) {
        itemData.map((val) => {
            if (val.name === params.serie!) {
                heading = ui[lang].Gallery.series + " '" + val.title[lang as Lang] + "'";
            }
        })
        itemData = getSeries(params.serie!);
    }

    // Separate the images in the series according to orientation
    let imageDataPortrait: ImageDesc[] = [];
    let imageDataLandscape: ImageDesc[] = [];
    itemData.map((item) => {
        if (item.format === landscape) imageDataLandscape.push(item);
        if (item.format === portrait) imageDataPortrait.push(item);
    });

    // Resort items so that portraits come first
    itemData = [];
    for (var i = 0; i < imageDataPortrait.length; i++) {
        itemData.push(imageDataPortrait[i]);
    }
    for (var i = 0; i < imageDataLandscape.length; i++) {
        itemData.push(imageDataLandscape[i]);
    }

    // Calculate number of rows with portraits
    const nrRowsPortrait = Math.floor((imageDataPortrait.length - 1) / 3) + 1;

    const fullsize = handle.active && isPainting;

    // Determine parameters for current painting (if any)
    let w = theme.painting.landscape.width;
    let h = theme.painting.landscape.height;
    let workdesc: Workdesc;
    if (isPainting) {
        workdesc = i18n(paintingName);
        if (workdesc.format === portrait) {
            w = theme.painting.portrait.width;
            h = theme.painting.portrait.height;
        }
        if (w * window.visualViewport!.height > h * window.visualViewport!.width) {
            h = h * window.visualViewport!.width / w;
            w = window.visualViewport!.width;
        }
        else {
            w = w * window.visualViewport!.height / h;
            h = window.visualViewport!.height;
        }
    }

    if (layoutState.isMobile) {
        return <GalleryMobile fullsize={fullsize} heading={heading} itemData={itemData} lang={lang} layoutState={layoutState} paintingName={paintingName} serie={params.serie}></GalleryMobile>
    }
    else {
        return <GalleryDesktop nrRowsPortrait={nrRowsPortrait} fullsize={fullsize} heading={heading} itemData={itemData} lang={lang} layoutState={layoutState} paintingName={paintingName} serie={params.serie}></GalleryDesktop>;
    };
}


